import {
  AssetIoConfigOutput,
  PinToPhaseMapping,
  SiteConfigurationInput,
  SiteConfigurationOutputReadable,
} from "../services/client";

type AnySiteConfiguration =
  | SiteConfigurationInput
  | SiteConfigurationOutputReadable;

type IoConfiguration = { asset_io_config: AssetIoConfigOutput[] };

type RotationCarrier = {
  name: string;
  phase_permutations?: unknown;
  children?: RotationCarrier[] | null;
};

export type PhaseRotationDeclarations = {
  declared: string[];
  undeclared: string[];
};

/**
 * Mirrors the controller's reading of a pole's pin mapping: unlisted pins keep
 * the identity phase, and the result only declares an orientation when it is a
 * bijection of the three phases.
 */
function pinMappingIsPhaseBijection(
  mapping: PinToPhaseMapping | null | undefined,
): boolean {
  if (mapping?.pin_list.length !== 3 || mapping?.phase_list.length !== 3) {
    return false;
  }
  const pinToPhase: Record<string, string> = {
    pin1: "L1",
    pin2: "L2",
    pin3: "L3",
  };
  const pins = mapping?.pin_list ?? [];
  const phases = mapping?.phase_list ?? [];
  for (let i = 0; i < Math.min(pins.length, phases.length); i++) {
    pinToPhase[pins[i]] = phases[i];
  }
  return new Set(Object.values(pinToPhase)).size === 3;
}

type AnyDevice = NonNullable<AnySiteConfiguration["device_list"]>[number];

type RotationDeclaration = { name: string; isDeclared: boolean };

function deviceRotationDeclarations(device: AnyDevice): RotationDeclaration[] {
  switch (device.type) {
    case "charging_pole_3phase":
      return [
        {
          name: device.name,
          isDeclared: pinMappingIsPhaseBijection(device.pin_to_phase_mapping),
        },
      ];
    case "double_charger":
      return [
        { name: device.name, isDeclared: true },
        ...device.connectors.map((connector) => ({
          name: connector.name,
          isDeclared:
            !!connector.pin_to_phase_mapping &&
            pinMappingIsPhaseBijection(connector.pin_to_phase_mapping),
        })),
      ];
    case "dc_charging_pole":
    case "forecasted_series_3phase":
    case "battery_energy_storage_system":
      return [{ name: device.name, isDeclared: !!device.phase_permutations }];
    case undefined:
      return [
        {
          name: device.name,
          isDeclared: !!(device as RotationCarrier).phase_permutations,
        },
      ];
  }
}

/**
 * Which assets declare a phase rotation and which don't: every collector below
 * the root, every device with `phase_permutations`, and AC poles through their
 * `pin_to_phase_mapping` (declared only when it maps pins onto all three
 * phases). A virtual collector only sums its children, so it counts as the
 * identity when it declares nothing. A double charger expands into such a
 * virtual collector with one AC pole per connector. The controller only runs
 * phase-aware (`use_topology`) when nothing is undeclared; otherwise it falls
 * back to the worst case for the whole site.
 */
export function phaseRotationDeclarations(
  configuration: AnySiteConfiguration | null | undefined,
  ioConfiguration: IoConfiguration | null | undefined,
): PhaseRotationDeclarations {
  const virtualCollectorNames = new Set(
    ioConfiguration?.asset_io_config
      .filter((entry) => entry.config.type === "virtual")
      .map((entry) => entry.asset_id),
  );
  const declared: string[] = [];
  const undeclared: string[] = [];
  const record = (name: string, isDeclared: boolean) =>
    (isDeclared ? declared : undeclared).push(name);
  const walkChildren = (collector: RotationCarrier) =>
    (collector.children ?? []).forEach((child) => {
      record(
        child.name,
        !!child.phase_permutations || virtualCollectorNames.has(child.name),
      );
      walkChildren(child);
    });

  // The roots themselves have no parent to rotate against.
  configuration?.collector_list?.forEach((root) =>
    walkChildren(root as RotationCarrier),
  );
  configuration?.device_list
    ?.flatMap(deviceRotationDeclarations)
    .forEach(({ name, isDeclared }) => record(name, isDeclared));
  return { declared, undeclared };
}

/** The first few names, with an "and N more" tail so messages stay readable. */
export function formatAssetNames(names: string[]): string {
  const shown = names.slice(0, 3).map((name) => `"${name}"`);
  const rest = names.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", ");
}
